import { type FC } from 'react';

import type { VerticalProductTabItemType } from '@weberus/shared-types';
import { ImageComponentForThumbnail } from '@weberus/ui/components/image-component-for-thumbnail';
import { StarRating, MembershipBadge, ProductBadge, Thumbnail } from '@yanolja-rnd/nol-design-system';

import { getImage } from '../../utils/get-image';

export interface VerticalProductTabItemProps extends VerticalProductTabItemType {
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const ThumbnailDisplay: FC<{
  imageUrl: VerticalProductTabItemProps['imageUrl'];
  className: string;
  children?: React.ReactNode;
  mobileRatio?: VerticalProductTabItemProps['mobileRatio'];
}> = ({ className, imageUrl, children, mobileRatio = '1_1' }) => {
  return (
    <Thumbnail
      alt=""
      className={className}
      image={imageUrl}
      imageComponent={ImageComponentForThumbnail}
      ratio={{
        mobile: mobileRatio,
        pc: '3_4',
      }}
      shape={{
        mobile: 'roundedRectangle_8',
        pc: 'roundedRectangle_16',
      }}
    >
      {children}
    </Thumbnail>
  );
};

const RatingDisplay: FC<{
  className: string;
  score: VerticalProductTabItemProps['score'];
  reviewCount: VerticalProductTabItemProps['reviewCount'];
}> = ({ className, score, reviewCount }) => {
  if (!score && !reviewCount) {
    return null;
  }
  return (
    <div className={className}>
      {!!score && (
        <span className="mr-2">
          <StarRating score={score} size="medium" />
        </span>
      )}
      {!!reviewCount && (
        <>
          {!score && <span className="mr-2 typography-body-14-bold">후기</span>}
          <span className="typography-body-14-regular">{reviewCount}</span>
        </>
      )}
    </div>
  );
};

/**
 * 위젯_상품형 > 상품형 탭
 * 테마(타이틀)+서브테마(탭) 구성으로 - 다양한 테마 상품 노출에 적합합니다
 * @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=3235-20629&m=dev}
 */
export const VerticalProductTabItem: FC<VerticalProductTabItemProps> = (props) => {
  const {
    title,
    action,
    discountRate,
    price,
    priceUnit,
    imageUrl,
    hasGoldClassBadge = false,
    rank,
    score,
    reviewCount,
    badgeText,
    description,
    mobileRatio = '1_1',
    onClick,
    generalTypeWidth = 305,
  } = props;

  const BadgeDisplay = (
    <div className="absolute left-4 top-4 pc:left-12 pc:top-12 flex *:mr-2">
      {!!hasGoldClassBadge && <MembershipBadge type="gold" />}
      {!!badgeText && <ProductBadge type="filled_blackop">{badgeText}</ProductBadge>}
    </div>
  );

  const priceDisplay = (
    <div className="flex flex-row-reverse flex-wrap items-center justify-end h-[2rem] mt-4 overflow-hidden pc:h-[2.4rem]">
      {price.isValid ? (
        <>
          <div className="flex items-center flex-shrink-0">
            <span className="typography-subtitle-16-bold pc:typography-subtitle-20-bold">{price.text}</span>
            {!!priceUnit && <span className="typography-body-13-bold pc:typography-subtitle-18-bold">{priceUnit}</span>}
          </div>
          {!!discountRate && (
            <span className="mr-4 typography-subtitle-16-regular text-text-accent-medium pc:typography-subtitle-20-regular">
              {discountRate}
            </span>
          )}
        </>
      ) : (
        <span className="typography-body-13-bold pc:typography-subtitle-18-bold text-text-neutral-sub whitespace-nowrap">
          {price.text}
        </span>
      )}
    </div>
  );

  const descriptionDisplay = !!description && (
    <div className="mt-4 overflow-hidden typography-body-12-regular text-text-neutral-sub pc:typography-subtitle-16-regular whitespace-nowrap pc:mt-2">
      {description}
    </div>
  );

  if (rank) {
    // 상품형탭_랭킹
    return (
      <a href={action.web} onClick={(e) => onClick?.(e)}>
        {/* [랭킹형] 모바일 뷰 */}
        <div className="relative flex items-start text-text-neutral-main pc:hidden">
          <img
            alt={`랭킹 ${rank}등`}
            className="block"
            src={getImage(`/static/images/rank${rank.padStart(2, '0')}m.svg`)}
          />
          <div className="flex flex-col mx-12 overflow-hidden grow">
            <div className="mb-6 overflow-hidden typography-body-14-regular-line-clamp-1 mt-10">{title}</div>
            <div className="flex flex-row-reverse flex-wrap items-center justify-end h-[2rem] overflow-hidden">
              {price.isValid ? (
                <>
                  <div className="flex items-center flex-shrink-0">
                    <span className="typography-subtitle-16-bold">{price.text}</span>
                    {!!priceUnit && <span className="typography-body-13-bold">{priceUnit}</span>}
                  </div>
                  {!!discountRate && (
                    <span className="mr-4 typography-subtitle-16-regular text-text-accent-medium">{discountRate}</span>
                  )}
                </>
              ) : (
                <span className="typography-body-13-bold text-text-neutral-sub whitespace-nowrap">{price.text}</span>
              )}
            </div>
            <RatingDisplay className="flex items-center mt-6" reviewCount={reviewCount} score={score} />
          </div>
          <ThumbnailDisplay className="relative size-90 shrink-0" imageUrl={imageUrl} mobileRatio={mobileRatio}>
            {BadgeDisplay}
          </ThumbnailDisplay>
        </div>
        {/* [랭킹형] PC 뷰 - slide 당 5개 노출 */}
        <div className="text-text-neutral-main w-[240px] hidden pc:block">
          <ThumbnailDisplay className="relative mb-16" imageUrl={imageUrl} mobileRatio={mobileRatio}>
            {BadgeDisplay}
            <img
              alt={`랭킹 ${rank}등`}
              className="absolute bottom-0 left-0 hidden pc:block"
              src={getImage(`/static/images/rank${rank.padStart(2, '0')}.svg`)}
            />
            <img
              alt={`랭킹 ${rank}등`}
              className="absolute bottom-0 left-0 block pc:hidden"
              src={getImage(`/static/images/rank${rank.padStart(2, '0')}m.svg`)}
            />
          </ThumbnailDisplay>
          <div className="pc:px-4">
            <div className="overflow-hidden typography-subtitle-18-regular-line-clamp-1">{title}</div>
            {priceDisplay}
            <RatingDisplay className="flex items-center mt-4" reviewCount={reviewCount} score={score} />
          </div>
        </div>
      </a>
    );
  }

  // 상품형탭_일반
  return (
    <a className="text-text-neutral-main mx-auto" href={action.web} onClick={(e) => onClick?.(e)}>
      {/* 모바일 뷰 */}
      <div className="text-text-neutral-main pc:hidden">
        <ThumbnailDisplay className="relative mb-10" imageUrl={imageUrl} mobileRatio={mobileRatio}>
          {BadgeDisplay}
        </ThumbnailDisplay>
        <div className="mb-4 overflow-hidden typography-body-14-regular-line-clamp-1">{title}</div>
        {descriptionDisplay}
        {priceDisplay}
      </div>
      {/* [랭킹형] PC 뷰 - slide 당 4개 노출 */}
      <div
        className="text-text-neutral-main w-[305px] hidden pc:block"
        style={{
          width: `${generalTypeWidth}px`,
        }}
      >
        <ThumbnailDisplay className="relative mb-16" imageUrl={imageUrl} mobileRatio={mobileRatio}>
          {BadgeDisplay}
        </ThumbnailDisplay>
        <div className="pc:px-4">
          <div className="overflow-hidden typography-subtitle-18-regular-line-clamp-1">{title}</div>
          {descriptionDisplay}
          {priceDisplay}
        </div>
      </div>
    </a>
  );
};
